import React, { useState } from "react";
import { MdEdit, MdFileDownload, MdRemoveCircle, MdAddCircle } from "react-icons/md";
import { Link, useParams } from "react-router-dom";

const ITDeclaration = ({ tdsData, hasDeclarations }) => {
  const { company_employee_id } = useParams();

  // Add local state to track open/collapsed rental entries
  const [openRentalRows, setOpenRentalRows] = useState({});

  const toggleRentalRow = (idx) => {
    setOpenRentalRows((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // If no declarations, show the "no data" state
  if (!hasDeclarations) {
    // Get financial year ID from the API response structure for no declarations
    const financialYearId = tdsData?.data?.financial_years?.financial_year_id;

    return (
      <div className="w-[75%] p-6 mt-6">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <h1 className="text-[18px] font-semibold text-gray-800">
              IT Declaration
            </h1>
          </div>
        </div>

        <div className="text-center py-20">
          <div className="mb-6">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <MdFileDownload className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Declaration Found
            </h3>
            <p className="text-gray-500 mb-6">
              This employee hasn't submitted any investment declaration yet.
            </p>
            <Link
              to={`/employee-details/${company_employee_id}/investments-and-proofs/investment-declaration/${financialYearId}/edit`}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              Submit Declaration
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const declarationData = tdsData?.data;
  const mainData = declarationData?.main_submission_data;
  const taxRegime =
    mainData?.employee_regime_selection?.regime === "old"
      ? "Old Tax Regime"
      : "New Tax Regime";

  // Get status from API and format it for display
  const getStatusDisplay = (status) => {
    switch (status) {
      case "APPROVAL_AWAITING":
        return {
          text: "Approval Awaiting",
          className: "bg-yellow-200 text-yellow-900",
        };
      case "REVIEWED":
        return { text: "Reviewed", className: "bg-green-200 text-green-900" };
      case "REJECTED":
        return { text: "Rejected", className: "bg-red-200 text-red-900" };
      default:
        return {
          text: "No Status",
          className: "",
        };
    }
  };

  const statusDisplay = getStatusDisplay(mainData?.status);

  // Transform API data for display
  const transformedData = {
    status: statusDisplay.text,
    statusClassName: statusDisplay.className,
    taxRegime,
    investments: [],
  };

  // Add House Rent if exists
  if (declarationData?.house_rent?.length > 0) {
    const houseRentItems = declarationData.house_rent.map((rent) => {
      const months = calculateMonthsDifference(rent.from_date, rent.to_date);
      const totalAmount = parseFloat(rent.rent_amount) * months;

      // Format dates
      const fromDate = new Date(rent.from_date).toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      });
      const toDate = new Date(rent.to_date).toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      });

      return {
        period: `${fromDate} - ${toDate}`,
        address: rent.address,
        landlordName: rent.landlord_name,
        landlordPan: rent.landlord_pan,
        monthlyRent: parseFloat(rent.rent_amount),
        declaredAmount: `₹${totalAmount.toLocaleString()}`,
        isMetro: rent.is_metro === "1",
      };
    });

    transformedData.investments.push({
      section: "House Rent Paid Details",
      items: houseRentItems,
      isHouseRent: true,
    });
  }

  // Add Other Income if exists (right after House Rent)
  if (declarationData?.other_source_income) {
    const otherIncome = declarationData.other_source_income;
    const incomeItems = [];

    if (
      otherIncome.income_from_other_sources &&
      parseFloat(otherIncome.income_from_other_sources) > 0
    ) {
      incomeItems.push({
        name: "Income from other sources",
        declaredAmount: `₹${parseFloat(
          otherIncome.income_from_other_sources
        ).toLocaleString()}`,
      });
    }

    if (otherIncome.interest_from_savings && parseFloat(otherIncome.interest_from_savings) > 0) {
      incomeItems.push({
        name: "Interest Earned from Savings Deposit",
        declaredAmount: `₹${parseFloat(otherIncome.interest_from_savings).toLocaleString()}`,
      });
    }

    if (otherIncome.interest_from_fixed_deposits && parseFloat(otherIncome.interest_from_fixed_deposits) > 0) {
      incomeItems.push({
        name: "Interest Earned from Fixed Deposit",
        declaredAmount: `₹${parseFloat(otherIncome.interest_from_fixed_deposits).toLocaleString()}`,
      });
    }

    if (otherIncome.interest_from_national_savings && parseFloat(otherIncome.interest_from_national_savings) > 0) {
      incomeItems.push({
        name: "Interest Earned from National Savings Certificates",
        declaredAmount: `₹${parseFloat(otherIncome.interest_from_national_savings).toLocaleString()}`,
      });
    }

    if (incomeItems.length > 0) {
      transformedData.investments.push({
        section: "Other Income",
        items: incomeItems,
        isOtherIncome: true,
      });
    }
  }

  // Add 80C investments if exists
  if (declarationData?.investments_80C?.length > 0) {
    const items = declarationData.investments_80C.map((investment) => ({
      name: investment.label, // Use label instead of ID
      declaredAmount: `₹${parseFloat(investment.amount).toLocaleString()}`,
    }));

    transformedData.investments.push({
      section: "80C Investments",
      items,
    });
  }

  // Add 80D exemptions if exists
  if (declarationData?.exemptions_80D?.length > 0) {
    const items = declarationData.exemptions_80D.map((exemption) => ({
      name: exemption.label, // Use label instead of ID
      declaredAmount: `₹${parseFloat(exemption.amount).toLocaleString()}`,
    }));

    transformedData.investments.push({
      section: "80D Exemptions",
      items,
    });
  }

  // Add Other Investments if exists
  if (declarationData?.other_investments?.length > 0) {
    const items = declarationData.other_investments.map((investment) => ({
      name: investment.label, // Use label instead of ID
      declaredAmount: `₹${parseFloat(investment.amount).toLocaleString()}`,
    }));

    transformedData.investments.push({
      section: "Other Investments & Exemptions",
      items,
    });
  }

  // Helper function to calculate months difference
  function calculateMonthsDifference(fromDate, toDate) {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const yearDiff = to.getFullYear() - from.getFullYear();
    const monthDiff = to.getMonth() - from.getMonth();
    return yearDiff * 12 + monthDiff + 1;
  }

  // Transform API data for display, regime-specific
  let investmentsSections = [];

  if (mainData?.employee_regime_selection?.regime === "old") {
    // Add House Rent if exists
    if (declarationData?.house_rent?.length > 0) {
      const houseRentItems = declarationData.house_rent.map((rent) => {
        const months = calculateMonthsDifference(rent.from_date, rent.to_date);
        const totalAmount = parseFloat(rent.rent_amount) * months;

        // Format dates
        const fromDate = new Date(rent.from_date).toLocaleDateString("en-GB", {
          month: "short",
          year: "numeric",
        });
        const toDate = new Date(rent.to_date).toLocaleDateString("en-GB", {
          month: "short",
          year: "numeric",
        });

        return {
          period: `${fromDate} - ${toDate}`,
          address: rent.address,
          landlordName: rent.landlord_name,
          landlordPan: rent.landlord_pan,
          monthlyRent: parseFloat(rent.rent_amount),
          declaredAmount: `₹${totalAmount.toLocaleString()}`,
          isMetro: rent.is_metro === "1",
        };
      });

      investmentsSections.push({
        section: "House Rent Paid Details",
        items: houseRentItems,
        isHouseRent: true,
      });
    }
    // Add Other Income if exists (right after House Rent)
    if (declarationData?.other_source_income) {
      const otherIncome = declarationData.other_source_income;
      const incomeItems = [];

      if (
        otherIncome.income_from_other_sources &&
        parseFloat(otherIncome.income_from_other_sources) > 0
      ) {
        incomeItems.push({
          name: "Income from other sources",
          declaredAmount: `₹${parseFloat(
            otherIncome.income_from_other_sources
          ).toLocaleString()}`,
        });
      }

      if (otherIncome.interest_from_savings && parseFloat(otherIncome.interest_from_savings) > 0) {
        incomeItems.push({
          name: "Interest Earned from Savings Deposit",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_savings).toLocaleString()}`,
        });
      }

      if (otherIncome.interest_from_fixed_deposits && parseFloat(otherIncome.interest_from_fixed_deposits) > 0) {
        incomeItems.push({
          name: "Interest Earned from Fixed Deposit",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_fixed_deposits).toLocaleString()}`,
        });
      }

      if (otherIncome.interest_from_national_savings && parseFloat(otherIncome.interest_from_national_savings) > 0) {
        incomeItems.push({
          name: "Interest Earned from National Savings Certificates",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_national_savings).toLocaleString()}`,
        });
      }

      if (incomeItems.length > 0) {
        investmentsSections.push({
          section: "Other Income",
          items: incomeItems,
          isOtherIncome: true,
        });
      }
    }
    // Add 80C investments if exists
    if (declarationData?.investments_80C?.length > 0) {
      const items = declarationData.investments_80C.map((investment) => ({
        name: investment.label, // Use label instead of ID
        declaredAmount: `₹${parseFloat(investment.amount).toLocaleString()}`,
      }));

      investmentsSections.push({
        section: "80C Investments",
        items,
      });
    }
    // Add 80D exemptions if exists
    if (declarationData?.exemptions_80D?.length > 0) {
      const items = declarationData.exemptions_80D.map((exemption) => ({
        name: exemption.label, // Use label instead of ID
        declaredAmount: `₹${parseFloat(exemption.amount).toLocaleString()}`,
      }));

      investmentsSections.push({
        section: "80D Exemptions",
        items,
      });
    }
    // Add Other Investments if exists
    if (declarationData?.other_investments?.length > 0) {
      const items = declarationData.other_investments.map((investment) => ({
        name: investment.label, // Use label instead of ID
        declaredAmount: `₹${parseFloat(investment.amount).toLocaleString()}`,
      }));

      investmentsSections.push({
        section: "Other Investments & Exemptions",
        items,
      });
    }
  } else if (mainData?.employee_regime_selection?.regime === "new") {
    // Only show Other Income for new regime
    if (declarationData?.other_source_income) {
      const otherIncome = declarationData.other_source_income;
      const incomeItems = [];
      if (
        otherIncome.income_from_other_sources &&
        parseFloat(otherIncome.income_from_other_sources) > 0
      ) {
        incomeItems.push({
          name: "Income from other sources",
          declaredAmount: `₹${parseFloat(
            otherIncome.income_from_other_sources
          ).toLocaleString()}`,
        });
      }
      if (otherIncome.interest_from_savings && parseFloat(otherIncome.interest_from_savings) > 0) {
        incomeItems.push({
          name: "Interest Earned from Savings Deposit",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_savings).toLocaleString()}`,
        });
      }
      if (otherIncome.interest_from_fixed_deposits && parseFloat(otherIncome.interest_from_fixed_deposits) > 0) {
        incomeItems.push({
          name: "Interest Earned from Fixed Deposit",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_fixed_deposits).toLocaleString()}`,
        });
      }
      if (otherIncome.interest_from_national_savings && parseFloat(otherIncome.interest_from_national_savings) > 0) {
        incomeItems.push({
          name: "Interest Earned from National Savings Certificates",
          declaredAmount: `₹${parseFloat(otherIncome.interest_from_national_savings).toLocaleString()}`,
        });
      }
      if (incomeItems.length > 0) {
        investmentsSections.push({
          section: "Other Income",
          items: incomeItems,
          isOtherIncome: true,
        });
      }
    }
  }

  // After investmentsSections is built for regimes, append Rental Income section (both regimes)
  const rentalIncomeArray = declarationData?.rental_income || [];
  if (rentalIncomeArray.length > 0) {
    // compute totals and transform items for display
    const baseTotal = rentalIncomeArray.reduce(
      (sum, it) => sum + (parseFloat(it.net_income_loss || 0) || 0),
      0
    );

    const homeLoanMain = declarationData?.home_loan || null;
    const mainHomeLoanInterest = parseFloat(homeLoanMain?.interest_amount || 0);

    // Zoho behaviour:
    // - For old regime, displayed total = sum(net_income_loss) - main home_loan interest
    // - For new regime, displayed total = sum(net_income_loss)
    const displayedTotal =
      mainData?.employee_regime_selection?.regime === "old"
        ? baseTotal - mainHomeLoanInterest
        : baseTotal;

    const homeLoanInterestDisplay =
      homeLoanMain && mainHomeLoanInterest > 0
        ? `₹${mainHomeLoanInterest.toLocaleString()}`
        : null;

    const rentalItems = rentalIncomeArray.map((it, idx) => ({
      key: it.rental_income_id || idx,
      title: `Net Income / Loss from Let Out Property - ${idx + 1}`,
      annual_rent_received: parseFloat(it.annual_rent_received || 0),
      municipal_taxes_paid: parseFloat(it.municipal_taxes_paid || 0),
      net_annual_value: parseFloat(it.net_annual_value || 0),
      standard_deduction: parseFloat(it.standard_deduction || 0),
      repaying_home_loan: it.repaying_home_loan === "1" || it.repaying_home_loan === 1 || it.repaying_home_loan === true,
      repaying_home_loan_data: {
        principal_amount: parseFloat(it.principal_amount || 0),
        interest_amount: parseFloat(it.interest_amount || 0),
        name_of_lender: it.name_of_lender || "",
        lender_pan: it.lender_pan || "",
      },
      net_income_loss: parseFloat(it.net_income_loss || 0),
      amountDisplay: `₹${(parseFloat(it.net_income_loss || 0)).toLocaleString()}`,
    }));

    // Append a dedicated section marker
    investmentsSections.push({
      section: "Net Income / Loss from House Property",
      isRentalIncome: true,
      summary: {
        totalNetFromLetOut: displayedTotal,
        baseTotal,
        homeLoanInterestDisplay,
        homeLoanLender: homeLoanMain?.name_of_lender || "",
        homeLoanLenderPan: homeLoanMain?.lender_pan || "",
      },
      items: rentalItems,
    });
  }

  return (
    <div className="w-[75%] p-6 mt-6">
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-3">
          <h1 className="text-[18px] font-semibold text-gray-800">
            IT Declaration
          </h1>
          <span
            className={`text-xs px-3 py-1 rounded ${transformedData.statusClassName}`}
          >
            {transformedData.status}
          </span>
          <button className="text-gray-600 hover:text-gray-800">
            <Link
              to={`/employee-details/${company_employee_id}/investments-and-proofs/investment-declaration/${mainData?.financial_year?.financial_year_id}/edit`}
            >
              <MdEdit className="w-4 h-4" />
            </Link>
          </button>
        </div>

        <div className="text-gray-600 text-[13px]">
          <span className="font-medium">Tax Regime :</span>{" "}
          {transformedData.taxRegime}
          <Link
            to={`/employee-details/${company_employee_id}/investments-and-proofs/investment-declaration/${mainData?.financial_year?.financial_year_id}/compare-tax-regimes`}
            className="ml-2 text-blue-600 hover:text-blue-800 text-[12px]"
          >
            (Change Tax Regime)
          </Link>
        </div>
      </div>

      {/* Table Header */}
      <div className="bg-gray-50 py-3 px-3 mb-6 rounded">
        <div className="flex w-full">
          <div
            style={{ width: "60%" }}
            className="uppercase text-[13px] font-semibold text-gray-600 tracking-wider"
          >
            PARTICULARS
          </div>
          <div
            style={{ width: "40%" }}
            className="uppercase text-[13px] font-semibold text-gray-600 tracking-wider text-right"
          >
            DECLARED AMOUNT
          </div>
        </div>
      </div>

      {/* Investment Sections */}
      <div className="space-y-8">
        {investmentsSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className={"pb-2 border-b border-gray-200"}>
            {/* Section Header */}
            <div className="flex w-full mb-2 px-3">
              <div style={{ width: "60%" }}>
                <span className="text-base font-semibold text-gray-800">
                  {section.section}
                </span>
              </div>
            </div>

            {/* Section Items */}
            <div>
              {section.isHouseRent ? (
                // House Rent specific layout
                section.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    className="py-4 px-3 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex w-full mb-1">
                      <div
                        style={{ width: "60%" }}
                        className="text-gray-700 text-sm font-medium"
                      >
                        {item.period}
                      </div>
                      <div
                        style={{ width: "40%" }}
                        className="text-gray-800 text-sm font-semibold text-right"
                      >
                        {item.declaredAmount}
                      </div>
                    </div>

                    {/* Monthly amount below declared amount */}
                    <div className="flex w-full mb-3">
                      <div style={{ width: "60%" }}></div>
                      <div
                        style={{ width: "40%" }}
                        className="text-gray-500 text-xs text-right"
                      >
                        (₹{item.monthlyRent.toLocaleString()} / month)
                      </div>
                    </div>

                    {/* Additional details */}
                    <div className="text-gray-600 text-xs ml-0">
                      <div className="mb-1">
                        <span className="font-medium">{item.address}</span>
                      </div>
                      <div className="mb-1">
                        <span className="text-gray-500">Landlord Name : </span>
                        <span className="font-medium">{item.landlordName}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">PAN : </span>
                        <span className="font-medium">{item.landlordPan}</span>
                        {item.isMetro && (
                          <span className="ml-2 text-blue-600">• Metro</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : section.isOtherIncome ? (
                // Other Income specific layout
                section.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    className="py-4 px-3"
                  >
                    <div className="flex w-full">
                      <div
                        style={{ width: "60%" }}
                        className="text-gray-700 text-sm"
                      >
                        {item.name}
                      </div>
                      <div
                        style={{ width: "40%" }}
                        className="text-gray-800 text-sm font-semibold text-right"
                      >
                        {item.declaredAmount}
                      </div>
                    </div>
                  </div>
                ))
              ) : section.isRentalIncome ? (
                // NEW: Rental Income display (collapsible per property)
                <div className="py-4 px-3">
                  {/* Summary lines */}
                  <div className="flex w-full mb-3">
                    <div style={{ width: "60%" }} className="text-gray-700 text-sm font-medium">
                      Total Income / Loss from House Property
                    </div>
                    <div style={{ width: "40%" }} className="text-gray-800 text-sm font-semibold text-right">
                      {section.summary.totalNetFromLetOut ? `₹${section.summary.totalNetFromLetOut.toLocaleString()}` : "0"}
                    </div>
                  </div>

                  {mainData?.employee_regime_selection?.regime === "old" && section.summary.homeLoanInterestDisplay && (
                    <div className="flex w-full mb-4">
                      <div style={{ width: "60%" }} className="text-gray-700 text-sm">
                        Interest Paid on Home Loan
                        {section.summary.homeLoanLender && (
                          <div className="text-xs text-gray-500 mt-1">
                            Lender: {section.summary.homeLoanLender} &nbsp; | &nbsp; PAN: {section.summary.homeLoanLenderPan}
                          </div>
                        )}
                      </div>
                      <div style={{ width: "40%" }} className="text-gray-800 text-sm font-semibold text-right">
                        {section.summary.homeLoanInterestDisplay}
                      </div>
                    </div>
                  )}

                  {/* Rental items (collapsible) */}
                  {section.items.map((item, idx) => (
                    <div key={item.key} className="mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => toggleRentalRow(idx)}
                            className="flex items-center gap-2 text-sm text-gray-700"
                            aria-expanded={!!openRentalRows[idx]}
                          >
                            {openRentalRows[idx] ? (
                              <MdRemoveCircle className="text-red-500 w-5 h-5" />
                            ) : (
                              <MdAddCircle className="text-blue-500 w-5 h-5" />
                            )}
                            <span className="font-medium text-gray-900">{item.title}</span>
                          </button>
                        </div>
                        <div className="text-gray-800 text-sm font-semibold text-right">
                          {item.amountDisplay}
                        </div>
                      </div>

                      {openRentalRows[idx] && (
                        <div className="pl-4 border-l-2 border-gray-100 mt-3 space-y-3">
                          <div className="flex justify-between text-sm text-gray-700">
                            <div>Annual Rent Received</div>
                            <div>₹{item.annual_rent_received.toLocaleString()}</div>
                          </div>
                          <div className="flex justify-between text-sm text-gray-700">
                            <div>Municipal Taxes Paid</div>
                            <div>₹{item.municipal_taxes_paid.toLocaleString()}</div>
                          </div>
                          <div className="flex justify-between text-sm text-gray-700">
                            <div>Net Annual Value</div>
                            <div>₹{item.net_annual_value.toLocaleString()}</div>
                          </div>
                          <div className="flex justify-between text-sm text-gray-700">
                            <div>Standard Deduction (@ 30% of Net Annual Value)</div>
                            <div>₹{item.standard_deduction.toLocaleString()}</div>
                          </div>

                          {item.repaying_home_loan && (
                            <>
                              <div className="flex justify-between text-sm text-gray-700">
                                <div>Interest Paid on Home Loan</div>
                                <div>₹{(item.repaying_home_loan_data.interest_amount || 0).toLocaleString()}</div>
                              </div>
                              <div className="text-xs text-gray-500">
                                Lender: {item.repaying_home_loan_data.name_of_lender || "-"} &nbsp; | &nbsp; PAN: {item.repaying_home_loan_data.lender_pan || "-"}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                // Regular items layout
                section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex w-full py-3 px-3">
                    <div
                      style={{ width: "60%" }}
                      className="text-gray-700 text-sm"
                    >
                      {item.name}
                    </div>
                    <div
                      style={{ width: "40%" }}
                      className="text-gray-800 text-sm font-semibold text-right"
                    >
                      {item.declaredAmount}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Download Section - Commented out as requested */}
      {/* 
      <div className="mt-4 pt-6 bg-gray-50 p-4 rounded">
        <div className="flex items-center justify-between">
          <span className="text-gray-700 text-sm font-medium">
            Form 12BB - Employee Investment Declaration
          </span>
          <button className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm">
            <MdFileDownload className="w-4 h-4" />
            Download
          </button>
        </div>
      </div>
      */}
    </div>
  );
};

export default ITDeclaration;
